import { Link, useLocation, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import Loadingmini from "../components/loadingmini";
import Vehiclecommands from "./vehiclecommands";
import Vehicleregistration from "./vehicleregistration";
import Vehiclevlo from "./vehiclevlo";
import Accidentinspector from "./accidentinspector";
import RateCheck from "./drivervehiclepricing";
import { fleetFromLocation, fetchVehicleLocation, fetchVehiclePage, money, updateVehiclePage } from "../components/staffpage";
import { withFleets } from "../withFleets";
import "../components/staffpage.css";

const SECTIONS = [
  ["info", "Info"],
  ["place", "Location"],
  ["commands", "Commands"],
  ["reservations", "Reservations"],
  ["status", "Status"],
  ["insurance", "Insurance"],
  ["health", "Health"],
  ["money", "Money"],
  ["docs", "Docs"],
  ["accidents", "Accidents"],
  ["json", "JSON"],
];

function Fact({ k, v, alert, onClick, to }) {
  const className = "staff-fact" + (alert ? " alert" : "");
  const body = <><span className="k">{k}</span><span className="v">{v}</span></>;
  if (to) return <Link className={className} to={to}>{body}</Link>;
  return <button type="button" className={className} onClick={onClick}>{body}</button>;
}

function textOf(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value.message) return String(value.message);
  try {
    return JSON.stringify(value);
  } catch (e) {
    return "Could not save";
  }
}

function savedText(value) {
  return value == null ? "" : String(value);
}

function useDraft(saved) {
  const next = savedText(saved);
  const [value, setValue] = useState(next);
  const [basis, setBasis] = useState(next);
  if (basis !== next) {
    setBasis(next);
    setValue(next);
  }
  return [value, setValue, value !== next];
}

function legacyVehicle(page) {
  const vehicle = (page && page.vehicle) || {};
  const insurance = (page && page.insurance) || {};
  return {
    resp: [{
      car_id: vehicle.id,
      name: vehicle.name,
      vin: vehicle.vin,
      license: vehicle.license,
      license_state: vehicle.licenseState,
      license_expire: vehicle.licenseExpire,
      address: vehicle.address,
      make_model_year: vehicle.makeModelYear,
      company: insurance.company,
    }],
  };
}

export default function VehicleNew() {
  const params = useParams();
  const location = useLocation();
  const vehicleId = params.vehicleId;
  const fleetCode = fleetFromLocation(location.search).split(",")[0];
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("info");
  const [jwt, setJwt] = useState("");
  const [canEdit, setCanEdit] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [bypassOffer, setBypassOffer] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const session = await Auth.currentSession();
      const token = session.getAccessToken();
      setJwt(token.getJwtToken());
      const groups = (token.payload && token.payload["cognito:groups"]) || [];
      const group = String(groups[0] || "");
      setCanEdit(group === "admin" || group === "support" || group === "fleet");
      setIsAdmin(group === "admin");
      if (!fleetCode) {
        setError("Select a fleet, then open this vehicle again.");
        setPage(null);
        return;
      }
      const body = await fetchVehiclePage({ fleetCode, vehicleId });
      if (!body || body.status !== "success") {
        setError((body && body.message) || "Could not load this vehicle.");
        setPage(null);
        return;
      }
      setPage(body);
    } catch (e) {
      setError(textOf(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [vehicleId, fleetCode]);

  async function save(action, fields, prompt) {
    if (!window.confirm(prompt || "Save this change?")) return null;
    setSaving(true);
    setNotice("");
    setError("");
    try {
      const body = await updateVehiclePage({ fleetCode, vehicleId, action, fields });
      if (body && body.status === "blocked") {
        setBypassOffer(!!body.bypassAvailable);
        setNotice(body.message || "That change is blocked.");
        return body;
      }
      setBypassOffer(false);
      if (!body || body.status !== "success") {
        setError((body && body.message) || "Could not save.");
        return body;
      }
      setPage(body);
      setNotice("Saved.");
      return body;
    } catch (e) {
      setError(textOf(e));
      return null;
    } finally {
      setSaving(false);
    }
  }

  const vehicle = page && page.vehicle;
  const place = (page && page.place) || {};
  const insurance = (page && page.insurance) || {};
  const financing = (page && page.financing) || {};
  const health = (page && page.health) || {};
  const bodyLock = section === "reservations" || section === "status" || section === "commands";

  return (
    <div className={"staff-page vehicle-screen" + (section === "commands" ? " commands-mode" : "")}>
      <header className="staff-head">
        <p className="staff-kicker">Vehicle</p>
        <h1 className="staff-title">{vehicle ? vehicle.name : ("Vehicle " + vehicleId)}</h1>
        {vehicle ? (
          <div className="staff-meta">
            {vehicle.makeModelYear ? <span>{vehicle.makeModelYear}</span> : null}
            {fleetCode ? <span>{fleetCode}</span> : null}
          </div>
        ) : null}
        {vehicle ? (
          <div className="staff-chips">
            <Fact k="Status" v={vehicle.status || "Unknown"} alert={String(vehicle.status || "").toLowerCase() !== "active"} onClick={() => setSection("status")} />
            {page && page.openReservation ? (
              <Fact
                k="Open reservation"
                v={[page.openReservation.status, page.openReservation.driverName].filter(Boolean).join(" · ") || page.openReservation.id}
                to={withFleets("/reservation/" + page.openReservation.id, fleetCode)}
              />
            ) : (
              <Fact k="Open reservation" v="None" onClick={() => setSection("reservations")} />
            )}
            <Fact k="Plate" v={[vehicle.license, vehicle.licenseState].filter(Boolean).join(" ") || "None"} alert={!vehicle.license} onClick={() => setSection("info")} />
            <Fact k="Home" v={vehicle.address || "None"} alert={!vehicle.address} onClick={() => setSection("info")} />
            <Fact k="Insurance" v={insurance.company || "None"} alert={!insurance.company} onClick={() => setSection("insurance")} />
            {place.sinceUpdate ? <Fact k="Last report" v={place.sinceUpdate} onClick={() => setSection("place")} /> : null}
            {health.status ? <Fact k="Health" v={health.status} alert={!/^(ok|good|healthy)$/i.test(health.status)} onClick={() => setSection("health")} /> : null}
          </div>
        ) : null}
        <div className="staff-chips">
          <button type="button" className="staff-save quiet" onClick={load}>Refresh</button>
        </div>
        {!vehicle || vehicle.address ? null : <p className="staff-note">An address is required before this vehicle is insured.</p>}
        {!vehicle || vehicle.license ? null : <p className="staff-note">A license plate and state are required before this vehicle can be set active.</p>}
        {notice ? <p className="staff-sentence">{notice}</p> : null}
        {error ? <p className="staff-note">{error}</p> : null}
      </header>
      {loading ? <Loadingmini /> : vehicle ? (
        <>
          <nav className="staff-actions">
            {SECTIONS.map(([id, label]) => (
              <button type="button" key={id} className={section === id ? "on" : ""} onClick={() => setSection(id)}>{label}</button>
            ))}
          </nav>
          <div className={"staff-body" + (bodyLock ? " staff-body-lock" : "")}>
            {section === "info" ? (
              <Info vehicle={vehicle} canEdit={canEdit} saving={saving} addresses={page.addresses || []} onSave={save} />
            ) : null}
            {section === "place" ? <Place place={place} highs={page.engineHighs || []} fleetCode={fleetCode} vehicleId={vehicleId} /> : null}
            {section === "commands" && canEdit ? (
              <div className="staff-fill">
                <div className="staff-commands-panel">
                  <Vehiclecommands userJwt={jwt} vehicleId={vehicleId} />
                </div>
                <CommandHistory rows={page.commands || []} />
              </div>
            ) : null}
            {section === "commands" && !canEdit ? <p className="staff-sentence">Commands are for fleet staff.</p> : null}
            {section === "reservations" ? <Reservations rows={page.reservations || []} fleetCode={fleetCode} /> : null}
            {section === "status" ? (
              <Status
                vehicle={vehicle}
                history={page.statusHistory || []}
                canEdit={canEdit}
                isAdmin={isAdmin}
                saving={saving}
                bypassOffer={bypassOffer}
                onSave={save}
              />
            ) : null}
            {section === "insurance" ? (
              <Insurance insurance={insurance} policies={page.policies || []} canEdit={canEdit} saving={saving} onSave={save} />
            ) : null}
            {section === "health" ? <Health health={health} /> : null}
            {section === "money" ? (
              <>
                <Financing financing={financing} companies={page.financingCompanies || []} canEdit={canEdit} saving={saving} onSave={save} />
                <RateCheck carId={vehicleId} fleetCode={fleetCode} />
              </>
            ) : null}
            {section === "docs" ? (
              <Docs page={page} fleetCode={fleetCode} />
            ) : null}
            {section === "accidents" ? (
              <Accidentinspector userJwt={jwt} refreshJwt={load} vehicle={legacyVehicle(page)} />
            ) : null}
            {section === "json" ? <pre className="staff-json">{JSON.stringify(page, null, 2)}</pre> : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

function Info({ vehicle, canEdit, saving, addresses, onSave }) {
  const [name, setName, nameChanged] = useDraft(vehicle.name);
  const [license, setLicense, licenseChanged] = useDraft(vehicle.license);
  const [licenseState, setLicenseState, stateChanged] = useDraft(vehicle.licenseState);
  const [licenseExpire, setLicenseExpire, expireChanged] = useDraft(vehicle.licenseExpire);
  const [addressId, setAddressId, addressChanged] = useDraft(vehicle.addressId);
  const plateChanged = licenseChanged || stateChanged || expireChanged;
  return (
    <>
      <dl className="staff-facts">
        <dt>ID</dt><dd>{vehicle.id}</dd>
        {vehicle.color ? <><dt>Color</dt><dd>{vehicle.color}</dd></> : null}
        {vehicle.region ? <><dt>Region</dt><dd>{vehicle.region}</dd></> : null}
        {vehicle.apiSource ? <><dt>Source</dt><dd>{vehicle.apiSource}</dd></> : null}
        <dt>Home</dt><dd>{vehicle.address || "None"}</dd>
      </dl>
      {canEdit ? (
        <>
          <form className="staff-form" onSubmit={(event) => { event.preventDefault(); onSave("name", { name }, "Save this name?"); }}>
            <label>Name<input value={name} onChange={(event) => setName(event.target.value)} /></label>
            <button className="staff-save" type="submit" disabled={saving || !nameChanged}>Save name</button>
          </form>
          <form className="staff-form" onSubmit={(event) => { event.preventDefault(); onSave("license", { license, licenseState, licenseExpire }, "Save this plate?"); }}>
            <label>Plate<input value={license} onChange={(event) => setLicense(event.target.value)} /></label>
            <label>State<input value={licenseState} onChange={(event) => setLicenseState(event.target.value)} /></label>
            <label>Expires<input value={licenseExpire} onChange={(event) => setLicenseExpire(event.target.value)} placeholder="YYYY-MM-DD" /></label>
            <button className="staff-save" type="submit" disabled={saving || !plateChanged}>Save plate</button>
          </form>
          <form className="staff-form" onSubmit={(event) => { event.preventDefault(); onSave("homespot", { addressId }, "Save this home address?"); }}>
            <label>Home address
              <select value={addressId} onChange={(event) => setAddressId(event.target.value)}>
                <option value="">Choose</option>
                {addresses.map((row) => (
                  <option key={row.id} value={row.id}>{row.address_full || row.name}</option>
                ))}
              </select>
            </label>
            <button className="staff-save" type="submit" disabled={saving || !addressChanged}>Save home</button>
          </form>
        </>
      ) : null}
    </>
  );
}

function Place({ place, highs, fleetCode, vehicleId }) {
  const [location, setLocation] = useState(null);
  const [locationNote, setLocationNote] = useState("");
  useEffect(() => {
    let live = true;
    setLocation(null);
    setLocationNote("");
    fetchVehicleLocation({ fleetCode, vehicleId })
      .then((body) => {
        if (!live) return;
        if (!body || body.status !== "success") {
          setLocationNote((body && body.message) || "Could not load the maps.");
          return;
        }
        setLocation(body);
        if (body.message) setLocationNote(body.message);
      })
      .catch((error) => {
        if (live) setLocationNote(textOf(error));
      });
    return () => { live = false; };
  }, [fleetCode, vehicleId]);
  const maps = location && location.closeMap && location.wideMap;
  return (
    <>
      <div className="staff-chips">
        {place.mapsUrl ? (
          <a className="staff-fact" href={place.mapsUrl} target="_blank" rel="noreferrer">
            <span className="k">Last position</span>
            <span className="v">{place.latitude}, {place.longitude}</span>
          </a>
        ) : <span className="staff-fact"><span className="k">Last position</span><span className="v">None</span></span>}
        {place.milesFromHome != null ? <span className="staff-fact"><span className="k">Miles from home</span><span className="v">{place.milesFromHome}</span></span> : null}
        {place.sinceUpdate ? <span className="staff-fact"><span className="k">Last report</span><span className="v">{place.sinceUpdate}</span></span> : null}
      </div>
      {maps ? (
        <>
          <h2 className="staff-section-title">GPS 48 hours from Last Known Position</h2>
          <div className="staff-maps">
            <img src={location.closeMap} alt="Close map of the last known position" />
            <img src={location.wideMap} alt="Map of the last 48 hours" />
          </div>
        </>
      ) : null}
      {location && location.telemetry === false ? (
        <>
          <p className="staff-sentence">This vehicle isn't sending telemetry yet.</p>
          <p className="staff-sentence">Contact support for assistance enrolling your vehicle.</p>
        </>
      ) : null}
      {!location && !locationNote ? <p className="staff-sentence">Loading maps.</p> : null}
      {locationNote ? <p className="staff-note">{locationNote}</p> : null}
      {highs.length ? (
        <div className="staff-cards">
          {highs.map((row, index) => (
            <article key={index}>
              <strong>{row.engineTemp}</strong>
              <p>{row.at}</p>
            </article>
          ))}
        </div>
      ) : <p className="staff-sentence">No engine temperature highs.</p>}
    </>
  );
}

function commandText(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
}

function CommandHistory({ rows }) {
  return (
    <>
      <h2 className="command-history-title">History</h2>
      <div className="staff-scroll command-history">
        {!rows.length ? <p className="staff-sentence">No commands yet.</p> : (
            <table className="pay-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Command</th>
                  <th>Send status</th>
                  <th>Command status</th>
                  <th>Driver</th>
                  <th>Error</th>
                  <th>Response</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td>{row.created}</td>
                    <td>{row.command}</td>
                    <td>{row.send_status}</td>
                    <td>{row.command_status}</td>
                    <td>{row.driver_name}</td>
                    <td>{commandText(row.error_info)}</td>
                    <td>{commandText(row.response)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
        )}
      </div>
    </>
  );
}

function Reservations({ rows, fleetCode }) {
  if (!rows.length) return <p className="staff-sentence">No reservations.</p>;
  return (
    <div className="staff-scroll">
      <div className="staff-cards">
        {rows.map((row) => (
          <article key={row.res_id + String(row.status)}>
            <Link to={withFleets("/reservation/" + row.res_id, fleetCode)}>{row.res_id}</Link>
            <p>{[row.status, row.start_string, row.end_string, row.driver_name].filter(Boolean).join(" · ")}</p>
          </article>
        ))}
      </div>
    </div>
  );
}

function Status({ vehicle, history, canEdit, isAdmin, saving, bypassOffer, onSave }) {
  const current = String(vehicle.status || "").toLowerCase();
  return (
    <div className="staff-fill">
      <p className="staff-sentence">Current status: {vehicle.status}</p>
      {canEdit ? (
        <div className="staff-row-actions">
          <button type="button" className="staff-save" disabled={saving || current === "active"} onClick={() => onSave("status", { status: "active" }, "Set this vehicle active?")}>Set active</button>
          <button type="button" className="staff-save quiet" disabled={saving || current === "inactive"} onClick={() => onSave("status", { status: "inactive" }, "Set this vehicle inactive?")}>Set inactive</button>
          {isAdmin ? <button type="button" className="staff-save quiet" disabled={saving || current === "retired"} onClick={() => onSave("status", { status: "retired" }, "Retire this vehicle?")}>Retire</button> : null}
          {bypassOffer ? (
            <button type="button" className="staff-save quiet" disabled={saving} onClick={() => onSave("status", { status: "active", bypassSuggested: true }, "Set this vehicle active and skip the recommended checks? Required setup still has to be in place.")}>Set active anyway</button>
          ) : null}
        </div>
      ) : null}
      <div className="staff-scroll">
        <div className="staff-cards">
          {history.map((row, index) => (
            <article key={index}>
              <strong>{row.status}</strong>
              <p>{row.sql_timestamp}</p>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

function Insurance({ insurance, policies, canEdit, saving, onSave }) {
  const [policyId, setPolicyId, policyChanged] = useDraft(insurance.policyId);
  return (
    <>
      <dl className="staff-facts">
        <dt>Company</dt><dd>{insurance.company || "None"}</dd>
        <dt>Policy</dt><dd>{insurance.policyNumber || "None"}</dd>
        {insurance.effective ? <><dt>Effective</dt><dd>{insurance.effective}</dd></> : null}
        {insurance.end ? <><dt>Ends</dt><dd>{insurance.end}</dd></> : null}
        {money(insurance.weekCost) ? <><dt>7-day cost</dt><dd>{money(insurance.weekCost)}</dd></> : null}
        {insurance.weekMiles != null ? <><dt>7-day miles</dt><dd>{insurance.weekMiles}</dd></> : null}
        {insurance.weekHours != null ? <><dt>7-day hours</dt><dd>{insurance.weekHours}</dd></> : null}
        {insurance.safetyBucket ? <><dt>Safety</dt><dd>{insurance.safetyBucket}</dd></> : null}
      </dl>
      {canEdit ? (
        <form className="staff-form" onSubmit={(event) => { event.preventDefault(); onSave("policy", { policyId }, "Save this policy?"); }}>
          <label>Primary policy
            <select value={policyId} onChange={(event) => setPolicyId(event.target.value)}>
              <option value="">Choose</option>
              {policies.map((row) => (
                <option key={row.id} value={row.id}>{[row.company, row.policy_number].filter(Boolean).join(" ")}</option>
              ))}
            </select>
          </label>
          <button className="staff-save" type="submit" disabled={saving || !policyChanged}>Save policy</button>
        </form>
      ) : null}
    </>
  );
}

function Health({ health }) {
  return (
    <>
      <p className="staff-sentence">{health.status || "No health status"}{health.description ? " — " + health.description : ""}</p>
      {health.components && health.components.length ? (
        <div className="staff-cards">
          {health.components.map((part) => <article key={part}><strong>{part}</strong></article>)}
        </div>
      ) : null}
    </>
  );
}

function Financing({ financing, companies, canEdit, saving, onSave }) {
  const [companyId, setCompanyId, companyChanged] = useDraft(financing.companyId);
  const [companyName, setCompanyName] = useState("");
  const [capitalCost, setCapitalCost, costChanged] = useDraft(financing.capitalCost);
  const [monthlyLeasePayment, setMonthlyLeasePayment, paymentChanged] = useDraft(financing.monthlyPayment);
  const [leaseStartDate, setLeaseStartDate, startChanged] = useDraft(financing.leaseStart);
  const [leaseTerm, setLeaseTerm, termChanged] = useDraft(financing.leaseTerm);
  const [leaseCode, setLeaseCode, codeChanged] = useDraft(financing.leaseCode);
  const [leaseTerminationDate, setLeaseTerminationDate, endChanged] = useDraft(financing.leaseEnd);
  const financingChanged = companyChanged || costChanged || paymentChanged || startChanged || termChanged || codeChanged || endChanged;
  return (
    <>
      <p className="staff-sentence">{financing.company ? ("Financing: " + financing.company) : "No financing company."}</p>
      {canEdit ? (
        <>
          <form className="staff-form" onSubmit={(event) => {
            event.preventDefault();
            onSave("financing", { financingCompanyId: companyId, capitalCost, monthlyLeasePayment, leaseStartDate, leaseTerm, leaseCode, leaseTerminationDate }, "Save this financing?");
          }}>
            <label>Company
              <select value={companyId} onChange={(event) => setCompanyId(event.target.value)}>
                <option value="">Choose</option>
                {companies.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
              </select>
            </label>
            <label>Capital cost<input value={capitalCost} onChange={(event) => setCapitalCost(event.target.value)} /></label>
            <label>Monthly payment<input value={monthlyLeasePayment} onChange={(event) => setMonthlyLeasePayment(event.target.value)} /></label>
            <label>Lease start<input value={leaseStartDate} onChange={(event) => setLeaseStartDate(event.target.value)} /></label>
            <label>Term<input value={leaseTerm} onChange={(event) => setLeaseTerm(event.target.value)} /></label>
            <label>Code<input value={leaseCode} onChange={(event) => setLeaseCode(event.target.value)} /></label>
            <label>Lease end<input value={leaseTerminationDate} onChange={(event) => setLeaseTerminationDate(event.target.value)} /></label>
            <button className="staff-save" type="submit" disabled={saving || !financingChanged}>Save financing</button>
          </form>
          <form className="staff-form" onSubmit={(event) => { event.preventDefault(); onSave("financingCompany", { financingCompanyName: companyName }, "Add this financing company?"); }}>
            <label>New company<input value={companyName} onChange={(event) => setCompanyName(event.target.value)} /></label>
            <button className="staff-save quiet" type="submit" disabled={saving || !companyName.trim()}>Add company</button>
          </form>
        </>
      ) : null}
    </>
  );
}

function Docs({ page, fleetCode }) {
  const files = page.files || {};
  const packet = legacyVehicle(page);
  return (
    <>
      <p className="staff-sentence">
        Registration {files.registration ? "is on file" : "is not on file"}. VLO {files.vlo ? "is on file" : "is not on file"}.
      </p>
      <Vehicleregistration vehicle={packet} fleetCode={fleetCode} />
      <Vehiclevlo vehicle={packet} fleetCode={fleetCode} />
    </>
  );
}
